import { Search } from 'lucide-react'
import { motion } from 'motion/react'
import { Progress } from '@/components/ui/progress'
import { staggerContainer, staggerItem } from '@/lib/motion/variants'
import type { SiteHealthAuditPhase } from '@/lib/siteHealth.types'
import { auditCoverageLabel, auditCoverageTarget } from './SiteHealthFormat'
import { EYEBROW_CLASS } from './SiteHealthStyles'
import type {
  ProgressStatProps,
  SiteHealthProgressProps,
} from './siteHealthComponents.types'

function runningPhaseLabel(phase: SiteHealthAuditPhase) {
  switch (phase) {
    case 'queued':
      return 'Queued'
    case 'crawling':
      return 'Crawling pages'
    case 'auditing':
      return 'Evaluating evidence'
    case 'finalizing':
      return 'Preparing report'
    default:
      return 'Audit in progress'
  }
}

export default function SiteHealthProgress({
  status,
}: SiteHealthProgressProps) {
  const progressBase = auditCoverageTarget(status)
  const progressPercent = Math.min(
    100,
    Math.round((status.auditedPages / progressBase) * 100)
  )

  return (
    <main className="mx-auto w-full max-w-3xl px-6 pb-20 pt-16">
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        animate="shown"
        className="rounded-2xl border border-border bg-card p-8 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <Search className="mt-1 size-7 text-primary" aria-hidden="true" />
          <div>
            <p className={EYEBROW_CLASS}>{runningPhaseLabel(status.phase)}</p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight">
              Site Health audit is running
            </h1>
          </div>
        </div>

        <Progress
          aria-label={`${progressPercent}% audited`}
          className="mt-8"
          value={progressPercent}
        />
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-4">
          <motion.div variants={staggerItem}>
            <ProgressStat label="Discovered" value={status.discoveredPages} />
          </motion.div>
          <motion.div variants={staggerItem}>
            <ProgressStat label="Received" value={status.receivedPages} />
          </motion.div>
          <motion.div variants={staggerItem}>
            <ProgressStat label="Audited" value={status.auditedPages} />
          </motion.div>
          <motion.div variants={staggerItem}>
            <ProgressStat label="Failed" value={status.failedPages} />
          </motion.div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          {auditCoverageLabel(status)}
        </p>
      </motion.section>
    </main>
  )
}

function ProgressStat({ label, value }: ProgressStatProps) {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
    </div>
  )
}
